import { formatMealPlanDate } from "@/helpers/useCommonFunctions";
import { RecipeImage, SORT_ORDER } from "@/utils/constant";
import { BreadcrumbData } from "@/utils/types/recipes";

// Parse Html
export const decodeHtml = (html: any) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.documentElement.textContent;
};

// Change algolia values to slug
export const toSlug = (str: string) => {
  const encodeSlug = str
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");

  return encodeURIComponent(encodeSlug);
};

// Print the Recipes detail
export const handlePrint = () => {
  window.print();
};

export const breadcrumbListRecipeDetails = (
  slug: string,
  title: string
): BreadcrumbData[] => [
  { text: "Home", href: "/" },
  { text: "Recipes", href: "/recipes" },
  { text: title ?? slug, href: "" },
];
//Returns a formatted metric string from quantity and unit.
export const buildMetricString = (item: any): string => {
  const metric = item?.quantity?.metric;
  const unit = item?.unit?.metric;

  if (typeof metric === "string" && metric.match(/[a-zA-Z]/)) {
    return metric;
  }
  if (metric && unit) {
    return `${metric} ${unit}`;
  }

  if (metric && !unit) {
    return `${metric} gm`; //Consider default to gram
  }

  return "";
};

//Parses a metric string and converts the value to grams.
export const convertToGm = (met: string): number => {
  if (!met) return 0;

  const match =
    typeof met === "string"
      ? met
          .trim()
          .toLowerCase()
          .match(/^([\d.]+)\s*(ml|l|gm|g|kg)?$/)
      : null;

  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2] || "gm";

  switch (unit) {
    case "l":
    case "kg":
      return value * 1000;
    case "ml":
    case "g":
    case "gm":
    default:
      return value;
  }
};
export const getSortedIngredient = (ingredient: any) => {
  const result: any = ingredient
    .map((item: any) => ({
      ...item,
      quantity: {
        ...item.quantity,
        MetInGm: convertToGm(item.quantity.Met),
      },
    }))
    .reduce((maxItem: any, item: any) => {
      return item.quantity.MetInGm > maxItem.quantity.MetInGm ? item : maxItem;
    });

  return result.ingredient;
};

export const createMealPayload = (
  mealTypeId: number,
  recipeId: string,
  token: string | null,
  scheduleDate?: string
) => ({
  type: "Add",
  meal_type_id: mealTypeId,
  recipe_ids: recipeId ? [recipeId] : [],
  token: token,
  schedule_date: scheduleDate || formatMealPlanDate(new Date()),
});

const fractionMap: Record<string, string> = {
  "1/2": "½",
  "1/3": "⅓",
  "2/3": "⅔",
  "1/4": "¼",
  "3/4": "¾",
  "1/5": "⅕",
  "2/5": "⅖",
  "3/5": "⅗",
  "4/5": "⅘",
  "1/6": "⅙",
  "5/6": "⅚",
  "1/8": "⅛",
  "3/8": "⅜",
  "5/8": "⅝",
  "7/8": "⅞",
};

export const formatQuantityValue = (value: string) => {
  return value.replace(/(\d)\/(\d)/, (_, numerator, denominator) => {
    const fraction = `${numerator}/${denominator}`;
    return fractionMap[fraction] || fraction;
  });
};

export const aemSortOrder = (aemFilterData: any) => {
  const attributes = aemFilterData?.attributes ?? [];

  const sortOrder = attributes
    .map((item: any) => SORT_ORDER.find((opt: any) => opt.name === item))
    .filter(Boolean);

  if (!attributes.includes("Tags")) {
    sortOrder.unshift({
      attribute: SORT_ORDER[0]?.attribute,
      name: "Tags",
    });
  }

  return sortOrder || [];
};

export const getFilteredRecipes = (originalRecipeList: any, value: string) => {
  const filtered = originalRecipeList
    .map((list: any) => {
      const matchingRecipes = list.recipes.filter((recipe: any) => {
        const searchValue = value.toLowerCase();

        // Check title match
        const titleMatch = recipe.title.toLowerCase().includes(searchValue);

        // Check ingredient match
        const ingredientMatch = recipe.ingredients?.some((ing: any) =>
          ing.ingredient.toLowerCase().includes(searchValue)
        );

        return titleMatch || ingredientMatch;
      });

      return matchingRecipes.length
        ? { ...list, recipes: matchingRecipes }
        : null;
    })
    .filter(Boolean);
  return filtered;
};

export const getFilterFavourites = (originalFavourites: any, value: string) => {
  const matchingRecipes = originalFavourites?.recipes.filter((recipe: any) => {
    const searchValue = value.toLowerCase();

    // Check title match
    const titleMatch = recipe.title.toLowerCase().includes(searchValue);

    // Check ingredient match
    const ingredientMatch = recipe.ingredients?.some((ing: any) =>
      ing.ingredient.toLowerCase().includes(searchValue)
    );

    return titleMatch || ingredientMatch;
  });

  return { ...originalFavourites, recipes: matchingRecipes };
};

export const updateListName = (lists: any, data: any) => {
  return lists.map((list: { id: any }) =>
    list.id === data?.id
      ? {
          ...list,
          list_name: data?.list_name,
        }
      : list
  );
};

interface Step {
  selected: boolean;
}

export function calculateProgress(steps: Step[]): number {
  const selectedCount = steps.filter((step) => step.selected).length;
  const totalCount = steps.length;
  const raw = totalCount > 0 ? (selectedCount / totalCount) * 100 : 0;
  return Math.ceil(raw);
}

const recipeArticlePreferenceMapping: any = {
  glutenFree: "Gluten Free",
  vegan: "Vegan",
  lactoseFree: "Lactose Free",
  vegetarian: "Vegetarian",
  kosher: "Kosher",
  lowSodium: "Low-sodium",
};

export const getPreferenceTags = (lifestyles: any) => {
  return Object.entries(lifestyles)
    .filter((val) => val[1] == true)
    .map((val) => `recipeTags: ${recipeArticlePreferenceMapping[val[0]]}`)
    .join(" OR ");
};

type Recipe = {
  title: string;
  created_at: string;
  [key: string]: any;
};

export const getSortedList = (
  recipeList: Recipe[],
  sortLabel: string
): Recipe[] => {
  switch (sortLabel) {
    // case "alphabetical":
    //   return [...recipeList].sort((a, b) =>
    //     a.list_name.localeCompare(b.list_name)
    //   );

    case "alphabetical_asc": // A → Z
      return [...recipeList].sort((a, b) =>
        a.list_name.localeCompare(b.list_name, undefined, { numeric: true })
      );

    case "alphabetical_desc": // Z → A
      return [...recipeList].sort((a, b) =>
        b.list_name.localeCompare(a.list_name, undefined, { numeric: true })
      );

    case "latest_to_oldest":
      return [...recipeList].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

    case "oldest_to_latest":
      return [...recipeList].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

    default:
      return [...recipeList].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  }
};
